'use client';

import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { IBlogItemEntry } from '../../../contentful/types/IBlogItemEntry';
import BlogCard from '../../../components/common/cards/Blog/BlogCard';
import { Section } from '../../content-sections/ui/components/Section';
import { Pagination } from '../../../components/common/Pagination';
import { PageRoutes } from '../../app/pages/domain/constants/PageRoutes';
import { ErrorMessages } from '../../../utils/errorMessages';
import { getBlogItemUrl } from '../domain/use-cases/getBlogItemUrl';
import { BlogCardContainer } from '../../../components/common/cards/Blog/BlogCardContainer';
import { useScrollIntoView } from '../../../hooks/useScrollIntoView';

interface IBlogsPageProps {
	pageNumber: number;
	totalPagesNumber: number;
	blogItems: IBlogItemEntry[];
}

export default function BlogsPage({
	blogItems,
	pageNumber,
	totalPagesNumber,
}: IBlogsPageProps) {
	const params = useParams();
	const { ref, scrollIntoView } = useScrollIntoView();

	useEffect(() => {
		// Trigger page scroll in response to pagination actions
		if (params?.pageIndex) {
			scrollIntoView();
		}
	}, [params?.pageIndex]);

	if (blogItems.length === 0) {
		return <ErrorMessage message={ErrorMessages.BlogItemsNotFound} />;
	}

	return (
		<Section ref={ref}>
			<BlogCardContainer>
				{blogItems.map((item) => (
					<BlogCard
						key={item.id}
						title={item.fields.title}
						body={item.fields.body}
						imageSource={item.fields.image?.fields.file.url}
						imageAlt={
							item.fields.image?.fields.description ??
							item.fields.image?.fields.title
						}
						linkHref={getBlogItemUrl(item.fields.slug)}
						publicationDate={item.fields.publicationDate}
					/>
				))}
			</BlogCardContainer>
			<Pagination
				pageNumber={pageNumber}
				totalPagesNumber={totalPagesNumber}
				root={PageRoutes.Blogs}
			/>
		</Section>
	);
}
